import React from "react";

import { get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { containerClasses, textareaClasses, wrapperClasses } from "./styles";

import type { JSX } from "react";

import type { InputProps } from "./types";

export default function Textarea({
  icon,
  name,
  label,
  sublabel,
  isRequired = false,
  isReadOnly = false,
  minLength = 0,
  defaultValue,
  className,
  isCompact,
  isNoWrap,
  validate,
  onBlurCustom,
  isTooltipErrorMessage,
  placeholder,
}: InputProps): JSX.Element {
  const {
    register,
    getFieldState,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation("editor-errors");

  const isInvalid = getFieldState(name)?.invalid;
  const error = get(errors, name);

  const { onBlur, ...rest } = register(name, {
    required: isRequired && t("requiredField"),
    minLength,
    validate,
  });

  return (
    <FieldContainer
      className={className}
      isCompact={isCompact}
      isNoWrap={isNoWrap}
    >
      {label ? (
        <Label
          isCompact={isCompact}
          isRequired={isRequired}
          label={label}
          name={name}
          sublabel={sublabel}
        />
      ) : null}
      <div className={wrapperClasses({ isCompact })}>
        <div className={containerClasses()}>
          <div className="text-muted-foreground pointer-events-none absolute inset-y-0 left-0 flex items-center pl-[0.46875rem]">
            {icon}
          </div>
          <textarea
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
            data-tooltip-content={error?.message}
            data-tooltip-id={`field-${name}-tooltip`}
            data-tooltip-position-strategy="fixed"
            defaultValue={defaultValue}
            disabled={isReadOnly}
            placeholder={placeholder}
            className={textareaClasses({
              icon,
              isInvalid,
              isReadOnly,
            })}
            onBlur={(e): void => {
              onBlur(e);
              onBlurCustom?.(e);
            }}
          />
        </div>
        <ErrorMessage
          error={error}
          isCompact={isCompact}
          isTooltip={isTooltipErrorMessage}
          name={name}
        />
      </div>
    </FieldContainer>
  );
}
